import {
  Context,
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as decode from 'jwt-decode';
import { AssumedIdentityData } from 'swo-ciam-common-react';
import { useQuery } from 'react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { RawAxiosRequestConfig } from 'axios';

import type { ProviderPropsInterface } from '@src/core/auth/components/types/provider-props.interface';
import {
  CachedSubscription,
  UserAccess,
  type TokenProviderContextInterface,
} from '@src/core/auth/components/types/token-provider-context.interface';
import { MonitoringSpanEnum } from '@src/core/monitoring/monitoring-span.enum';
import { Claims } from '@src/core/auth/components/types/claims.enum';
import { useInjectableServices } from '@src/server/service-registry.provider';
import {
  DEFAULT_USER_ACCESS,
  getAutomationAccess,
  getCiamAccess,
  getMyProjectsAccess,
  getPublisherUpdatesAccess,
  getReportsAccess,
  getRequestsCatalogAccess,
  getSupportSystemAccess,
} from '@src/core/auth/components/utils/permissions';
import { DecodedToken, SCU } from './types/accessToken';
import SplunkRum from '../../../../splunk.config';

export const toEffectiveToken = (decoded: DecodedToken): DecodedToken => {
  const assumedEmail = decoded.ciam_emulated_user?.assumedEmail;
  if (!assumedEmail) {
    return decoded;
  }

  const tenants = decoded.ciam_emulated_tenants ?? [];
  const scu = tenants
    .filter((tenant) => Boolean(tenant.scu))
    .map(
      (tenant) =>
        ({
          customerSCU: tenant.scu,
          customerName: tenant.name ?? '',
          azureCloud: [],
          keyVault: { baseUrl: '' },
          mailAddress: '',
          pycTenantId: '',
        } as SCU)
    );

  return {
    ...decoded,
    // eslint-disable-next-line camelcase
    ciam_permissions: decoded.ciam_emulated_permissions ?? [],
    // eslint-disable-next-line camelcase
    ciam_roles: decoded.ciam_emulated_roles ?? [],
    // eslint-disable-next-line camelcase
    ciam_tenants: tenants,
    [Claims.SCU]: scu,
  };
};

export const TokenProviderContext: Context<TokenProviderContextInterface> = createContext(null);

const TIMER_SCALAR_FOR_SECONDS = 1000;
const TOKEN_REFRESH_WINDOW_IN_SECONDS = 60;

export const getTokenRefreshDelay = (expiresIn?: number): number | undefined => {
  if (typeof expiresIn !== 'number' || !Number.isFinite(expiresIn)) {
    return undefined;
  }

  return Math.max((expiresIn - TOKEN_REFRESH_WINDOW_IN_SECONDS) * TIMER_SCALAR_FOR_SECONDS, 0);
};

export interface TokenProviderPropsInterface {
  updateHttpClient: (config: RawAxiosRequestConfig) => void;
}
export const TokenProvider: FC<ProviderPropsInterface & TokenProviderPropsInterface> = (props) => {
  const { UserService } = useInjectableServices();
  const { children, updateHttpClient } = props;

  const [isContextSettled, setIsContextSettled] = useState(false);
  const [isFullyAuthenticated, setIsFullyAuthenticated] = useState(false);

  const { data: user } = useQuery(['get-user'], () => UserService.getUser(), {
    enabled: isFullyAuthenticated,
    cacheTime: 10 * 60 * 1000, // 10 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const { getAccessTokenSilently, isLoading, isAuthenticated, logout } = useAuth0();
  const [forceSilentRefreshToken, setForceSilentRefreshToken] = useState(false);
  const accessTokenExpiresAtRef = useRef<number>();
  const refreshTokenTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isRefreshingTokenRef = useRef(false);

  const [rawAccessToken, setRawAccessToken] = useState<string>();

  const [userAccess, setUserAccess] = useState<UserAccess>(DEFAULT_USER_ACCESS);
  const [assumedIdentityData, setAssumedIdentityData] = useState<AssumedIdentityData>(
    {} as AssumedIdentityData
  );

  const [scus, setScus] = useState([]);

  const [cachedSubscriptions, setCachedSubscriptions] = useState<CachedSubscription>();

  const clearRefreshTokenTimer = useCallback(() => {
    if (refreshTokenTimerRef.current) {
      clearTimeout(refreshTokenTimerRef.current);
      refreshTokenTimerRef.current = undefined;
    }
  }, []);

  const scheduleRefreshTokenCallback = useCallback(
    (expiresIn?: number) => {
      clearRefreshTokenTimer();

      const refreshDelay = getTokenRefreshDelay(expiresIn);

      if (refreshDelay === undefined) {
        accessTokenExpiresAtRef.current = undefined;
        return;
      }

      accessTokenExpiresAtRef.current = Date.now() + expiresIn * TIMER_SCALAR_FOR_SECONDS;

      refreshTokenTimerRef.current = setTimeout(() => {
        refreshTokenTimerRef.current = undefined;
        setForceSilentRefreshToken(true);
      }, refreshDelay);
    },
    [clearRefreshTokenTimer]
  );

  const isAccessTokenExpiring = useCallback(() => {
    if (!accessTokenExpiresAtRef.current) {
      return false;
    }

    return (
      accessTokenExpiresAtRef.current - Date.now() <=
      TOKEN_REFRESH_WINDOW_IN_SECONDS * TIMER_SCALAR_FOR_SECONDS
    );
  }, []);

  const clearAuthenticationState = useCallback(() => {
    clearRefreshTokenTimer();
    accessTokenExpiresAtRef.current = undefined;
    updateHttpClient({ headers: {} });
    setIsFullyAuthenticated(false);
    setRawAccessToken(undefined);
    setIsContextSettled(true);
  }, [clearRefreshTokenTimer, updateHttpClient]);

  const handleTokenRefreshFailure = useCallback(() => {
    clearAuthenticationState();
    logout({ returnTo: window.location.origin });
  }, [clearAuthenticationState, logout]);

  useEffect(
    () => () => {
      clearRefreshTokenTimer();
    },
    [clearRefreshTokenTimer]
  );

  const refreshTokenOnResume = useCallback(() => {
    if (!isAuthenticated || !isFullyAuthenticated || !isAccessTokenExpiring()) {
      return;
    }

    if (document.visibilityState && document.visibilityState !== 'visible') {
      return;
    }

    setForceSilentRefreshToken(true);
  }, [isAuthenticated, isFullyAuthenticated, isAccessTokenExpiring]);

  useEffect(() => {
    document.addEventListener('visibilitychange', refreshTokenOnResume);
    window.addEventListener('focus', refreshTokenOnResume);

    return () => {
      document.removeEventListener('visibilitychange', refreshTokenOnResume);
      window.removeEventListener('focus', refreshTokenOnResume);
    };
  }, [refreshTokenOnResume]);

  const instrumentSplunkWithUserMetadata = useCallback(() => {
    if (!user) {
      return;
    }
    SplunkRum.setGlobalAttributes({
      [MonitoringSpanEnum.EndUserId]: user.email,
    });
  }, [user]);

  const instrumentHeapWithUserMetadata = useCallback(() => {
    if (!user) {
      return;
    }
    // @ts-ignore
    const Heap = globalThis.heap;

    Heap.identify(user.email);
  }, [user]);

  const getClientInformation = useCallback((token: DecodedToken) => {
    const userScus = token[Claims.SCU].filter(({ customerSCU }) => Boolean(customerSCU));
    setScus(userScus);

    const [userOnboardInfo] = userScus || [];

    if (userOnboardInfo) {
      setCachedSubscriptions({
        azureCloud: userOnboardInfo.azureCloud,
        customerName: userOnboardInfo.customerName,
        customerSCU: userOnboardInfo.customerSCU,
      });
    } else {
      setCachedSubscriptions(undefined);
    }
  }, []);

  const getAssumedIdentityData = useCallback((decoded: DecodedToken) => {
    const assumedIdentity: AssumedIdentityData =
      decoded?.ciam_emulated_user || ({} as AssumedIdentityData);

    setAssumedIdentityData(assumedIdentity);
  }, []);

  const updateHttpClientHeaders = useCallback(
    (accessToken: string) => {
      updateHttpClient({ headers: { Authorization: `Bearer ${accessToken}` } });
    },
    [updateHttpClient]
  );

  const getToken = useCallback(
    async (forceRefresh = false) => {
      if (isRefreshingTokenRef.current) {
        return;
      }

      isRefreshingTokenRef.current = true;
      setIsContextSettled(false);

      try {
        const { access_token: accessToken, expires_in: expiresIn } = await getAccessTokenSilently({
          detailedResponse: true,
          prompt: 'none',
          cacheMode: 'on',
          ignoreCache: forceRefresh,
        });
        const decodedToken = decode.default(accessToken) as DecodedToken;
        if (!decodedToken) {
          setIsContextSettled(true);
          return;
        }
        const effectiveToken = toEffectiveToken(decodedToken);

        instrumentSplunkWithUserMetadata();
        instrumentHeapWithUserMetadata();

        scheduleRefreshTokenCallback(expiresIn);
        updateHttpClientHeaders(accessToken);

        setIsFullyAuthenticated(Boolean(accessToken));
        setRawAccessToken(accessToken);

        setUserAccess({
          ...getSupportSystemAccess(effectiveToken),
          ...getCiamAccess(effectiveToken),
          ...getAutomationAccess(effectiveToken),
          ...getReportsAccess(effectiveToken),
          ...getRequestsCatalogAccess(effectiveToken),
          ...getPublisherUpdatesAccess(effectiveToken),
          ...getMyProjectsAccess(effectiveToken),
        });
        getAssumedIdentityData(decodedToken);

        getClientInformation(effectiveToken);

        setIsContextSettled(true);
      } catch {
        handleTokenRefreshFailure();
      } finally {
        isRefreshingTokenRef.current = false;
      }
    },
    [
      instrumentSplunkWithUserMetadata,
      instrumentHeapWithUserMetadata,
      scheduleRefreshTokenCallback,
      updateHttpClientHeaders,
      getAssumedIdentityData,
      getClientInformation,
      getAccessTokenSilently,
      handleTokenRefreshFailure,
    ]
  );

  useEffect(() => {
    if (!isLoading && !isFullyAuthenticated && isAuthenticated) {
      getToken();
    }
  }, [isLoading, isFullyAuthenticated, isAuthenticated, getToken]);

  useEffect(() => {
    if (forceSilentRefreshToken) {
      setForceSilentRefreshToken(false);
      getToken(true);
    }
  }, [forceSilentRefreshToken, getToken]);

  const tokenContext: TokenProviderContextInterface = useMemo(
    () => ({
      isFullyAuthenticated,
      isContextSettled,
      user,
      rawAccessToken,
      assumedIdentityData,
      isEmulating: Boolean(assumedIdentityData?.assumedEmail),
      ...userAccess,
      scus,
      cachedSubscriptions,
    }),
    [
      isFullyAuthenticated,
      isContextSettled,
      user,
      rawAccessToken,
      assumedIdentityData,
      userAccess,
      scus,
      cachedSubscriptions,
    ]
  );

  return (
    <TokenProviderContext.Provider value={tokenContext}>
      {children && children}
    </TokenProviderContext.Provider>
  );
};
